// src/components/padrekko/qc/MapAnnotationTool.jsx
//
// PadRekko "Annotations" tool. The command-rail Annotations button toggles this
// menu HUD; the user picks Arrow or Text, then draws directly on the map:
//   - Text  → click an anchor → type content inline → save (Point).
//   - Arrow → click tail → click head → save (LineString), with a live preview
//     line while placing. Self-contained — does NOT engage the ruler.
//
// Saves via the shared saveMapAnnotation path; renders through
// PersistentMeasurementsLayer.

import { useEffect, useState } from "react";
import { Type, ArrowUpRight, Loader2, X as XIcon } from "lucide-react";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import { useQcStore } from "../../../stores/useQcStore";
import { useAnnotationToolStore } from "../../../stores/useAnnotationToolStore";

const AMBER = "#f59e0b";
const DRAFT_SRC = "pk-annot-draft";
const EMPTY_FC = { type: "FeatureCollection", features: [] };

export default function MapAnnotationTool() {
  const mapInstance = useAppViewStore((s) => s.mapInstance);
  const session = useQcStore((s) => s.session);
  const currentPadIdx = useQcStore((s) => s.currentPadIdx);
  const saveMapAnnotation = useQcStore((s) => s.saveMapAnnotation);
  const saveInflight = useQcStore((s) => s.mapAnnotationSaveInflight);

  const menuOpen = useAnnotationToolStore((s) => s.menuOpen);
  const drawType = useAnnotationToolStore((s) => s.drawType);
  const closeMenu = useAnnotationToolStore((s) => s.closeMenu);
  const startDraw = useAnnotationToolStore((s) => s.startDraw);
  const stopDraw = useAnnotationToolStore((s) => s.stopDraw);

  const pad = session?.pads?.[currentPadIdx] ?? null;
  const findings = pad?.findings ?? [];
  const options = [{ key: "case", label: "Case", pad_id: null, finding_id: null }];
  if (pad) {
    options.push({ key: `pad:${pad.id}`, label: `Pad: ${pad.pad_name}`, pad_id: pad.id, finding_id: null });
    for (const f of findings) {
      options.push({ key: `finding:${f.id}`, label: `Finding ${f.idx + 1}`, pad_id: pad.id, finding_id: f.id });
    }
  }
  const defaultKey = pad ? `pad:${pad.id}` : "case";

  const required = drawType === "arrow" ? 2 : drawType === "text" ? 1 : 0;

  // Per-draw state.
  const [points, setPoints] = useState([]); // [[lng,lat], ...]
  const [cursor, setCursor] = useState(null); // [lng,lat] live preview head
  const [screen, setScreen] = useState(null); // save-popup screen position
  const [label, setLabel] = useState("");
  const [targetKey, setTargetKey] = useState(defaultKey);
  const [error, setError] = useState(null);

  const ready = !!drawType && points.length >= required; // geometry complete → save popup

  // Reset per-draw state whenever the draw mode changes (incl. cancel → null).
  // Render-time prop-change adjustment (avoids setState-in-effect).
  const [prevDrawType, setPrevDrawType] = useState(drawType);
  if (prevDrawType !== drawType) {
    setPrevDrawType(drawType);
    setPoints([]); setCursor(null); setScreen(null); setLabel(""); setError(null); setTargetKey(defaultKey);
  }

  // Collect map clicks (and arrow cursor preview) until the geometry is complete.
  useEffect(() => {
    if (!mapInstance || !drawType || ready) return undefined;
    mapInstance.getCanvas().style.cursor = "crosshair";
    function onClick(e) {
      const pt = [e.lngLat.lng, e.lngLat.lat];
      setPoints((prev) => {
        const next = [...prev, pt];
        if (next.length >= required) setScreen({ x: e.point.x, y: e.point.y });
        return next;
      });
    }
    function onMove(e) {
      if (drawType === "arrow") setCursor([e.lngLat.lng, e.lngLat.lat]);
    }
    mapInstance.on("click", onClick);
    mapInstance.on("mousemove", onMove);
    return () => {
      mapInstance.off("click", onClick);
      mapInstance.off("mousemove", onMove);
      const c = mapInstance.getCanvas?.();
      if (c) c.style.cursor = "";
    };
  }, [mapInstance, drawType, ready, required]);

  // Draft preview source/layer — added once, removed on unmount.
  useEffect(() => {
    if (!mapInstance) return undefined;
    if (!mapInstance.getSource(DRAFT_SRC)) {
      mapInstance.addSource(DRAFT_SRC, { type: "geojson", data: EMPTY_FC });
    }
    if (!mapInstance.getLayer("pk-annot-draft-line")) {
      mapInstance.addLayer({
        id: "pk-annot-draft-line", type: "line", source: DRAFT_SRC,
        paint: { "line-color": AMBER, "line-width": 2.5, "line-dasharray": [2, 1.5] },
      });
    }
    return () => {
      if (mapInstance.getLayer("pk-annot-draft-line")) mapInstance.removeLayer("pk-annot-draft-line");
      if (mapInstance.getSource(DRAFT_SRC)) mapInstance.removeSource(DRAFT_SRC);
    };
  }, [mapInstance]);

  // Feed the draft line (arrow: tail → head/cursor).
  useEffect(() => {
    if (!mapInstance) return;
    const src = mapInstance.getSource(DRAFT_SRC);
    if (!src) return;
    let coords = null;
    if (drawType === "arrow" && points.length >= 1) {
      const head = points[1] ?? cursor;
      if (head) coords = [points[0], head];
    }
    src.setData(coords ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "LineString", coordinates: coords } }] } : EMPTY_FC);
  }, [mapInstance, drawType, points, cursor]);

  // Keep the save popup pinned over the geometry's last point as the map moves.
  useEffect(() => {
    if (!mapInstance || !ready || !points.length) return undefined;
    function reposition() {
      const p = mapInstance.project(points[points.length - 1]);
      setScreen({ x: p.x, y: p.y });
    }
    mapInstance.on("move", reposition);
    return () => mapInstance.off("move", reposition);
  }, [mapInstance, ready, points]);

  function pick(type) { startDraw(type); }
  function cancel() { stopDraw(); } // local state resets via the prevDrawType tracker

  async function save() {
    const chosen = options.find((o) => o.key === targetKey) ?? options[0];
    let geometry;
    const extra = {};
    if (drawType === "text") {
      if (!label.trim()) { setError("Enter some text."); return; }
      geometry = { type: "Point", coordinates: points[0] };
      extra.label = label.trim();
    } else {
      geometry = { type: "LineString", coordinates: [points[0], points[1]] };
      if (label.trim()) extra.label = label.trim();
    }
    const res = await saveMapAnnotation({
      annotation_type: drawType,
      geometry,
      pad_id: chosen.pad_id,
      finding_id: chosen.finding_id,
      ...extra,
    });
    if (res.ok) stopDraw();
    else setError(res.error || "Failed to save.");
  }

  const placingHint = drawType && !ready;
  const hintText = drawType === "arrow"
    ? (points.length === 0 ? "Click the arrow's start point…" : "Click the arrow's end point…")
    : "Click the map to place text…";

  return (
    <>
      {/* Menu HUD beside the Annotations rail button. */}
      {menuOpen && (
        <div className="absolute z-[80] top-[281px] left-[48px] rounded-sm border border-amber-500/60
                        bg-white/95 shadow-md px-2 py-1.5 text-[11px] text-slate-700">
          <div className="flex items-center justify-between mb-1.5 gap-4">
            <span className="font-semibold text-amber-700">Annotation</span>
            <button type="button" onClick={closeMenu} className="text-slate-400 hover:text-slate-700" title="Close">
              <XIcon size={12} />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => pick("arrow")}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-slate-300
                         bg-white text-slate-600 hover:bg-slate-50 transition">
              <ArrowUpRight size={12} /> Arrow
            </button>
            <button type="button" onClick={() => pick("text")}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-slate-300
                         bg-white text-slate-600 hover:bg-slate-50 transition">
              <Type size={12} /> Text
            </button>
          </div>
        </div>
      )}

      {/* Placing hint while collecting clicks. */}
      {placingHint && (
        <div className="absolute z-[80] top-[281px] left-[48px] rounded-sm border border-amber-500/60
                        bg-white/95 shadow-md px-2 py-1 text-[10px] text-amber-700">
          {hintText}
          <button type="button" onClick={cancel} className="ml-2 text-slate-400 hover:text-slate-700">
            <XIcon size={11} className="inline" />
          </button>
        </div>
      )}

      {/* Save popup once the geometry is complete. */}
      {ready && screen && (
        <div
          className="absolute z-[90] w-[210px] rounded border border-amber-500/60 bg-white/95
                     shadow-lg px-2 py-1.5 text-[11px] text-slate-700"
          style={{
            left: Math.max(8, Math.min(screen.x + 8, (mapInstance?.getCanvas?.()?.clientWidth ?? 9999) - 220)),
            top: Math.max(8, screen.y + 8),
          }}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-amber-700">
              {drawType === "arrow" ? "Arrow annotation" : "Text annotation"}
            </span>
            <button type="button" onClick={cancel} className="text-slate-400 hover:text-slate-700" title="Cancel">
              <XIcon size={12} />
            </button>
          </div>
          <input
            autoFocus
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } else if (e.key === "Escape") cancel(); }}
            placeholder={drawType === "arrow" ? "Label (optional)…" : "Label text…"}
            className="w-full text-[11px] px-1.5 py-1 mb-1.5 rounded border border-slate-300 bg-white
                       text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-amber-500"
          />
          <select
            value={targetKey}
            onChange={(e) => setTargetKey(e.target.value)}
            className="w-full text-[11px] px-1.5 py-1 mb-1.5 rounded border border-slate-300 bg-white
                       text-slate-700 focus:outline-none focus:border-amber-500"
          >
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          {error && <div className="text-[10px] text-red-600 mb-1.5 leading-snug">{error}</div>}
          <div className="flex justify-end gap-1.5">
            <button type="button" onClick={cancel} disabled={saveInflight}
              className="text-[10px] px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={saveInflight}
              className="flex items-center gap-1 text-[10px] px-2.5 py-1 rounded border
                         bg-amber-500/15 border-amber-500/60 text-amber-700 hover:bg-amber-500/25 disabled:opacity-40">
              {saveInflight && <Loader2 size={11} className="animate-spin" />}
              {saveInflight ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
